import { CharacterAvatar } from "@/components/character/CharacterAvatar";
import { ChatRatingModal } from "@/components/ChatRatingModal";
import { ChatTextArea } from "@/components/ChatTextArea";
import { MessageBubble } from "@/components/MessageBubble";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/auth/useAuth";
import { useCharacter } from "@/hooks/useCharacter";
import { useChatHistory } from "@/hooks/useChatHistory";
import { useVisualViewport } from "@/hooks/useVisualViewport";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useIsFirstRender, useLocalStorage } from "@uidotdev/usehooks";
import { ArrowLeft, Loader2, MoreVertical, Send } from "lucide-react";
import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Virtualizer, type VirtualizerHandle } from "virtua";

const AnsweringBubble = () => (
  <div className="animate-fade-in flex justify-start py-1.5">
    <div className="relative flex max-w-xs items-end sm:max-w-sm">
      <div className="rounded-tl-xs shadow-xs relative flex items-center gap-2 rounded-2xl border border-gray-200 bg-white/90 px-4 pb-3 pt-2 text-sm text-gray-900">
        <div className="flex h-[17px] items-end">
          <span
            className="mr-0.5 inline-block h-1 w-1 rounded bg-blue-500 [animation:typing-dot_1.5s_infinite_ease-in-out]"
            style={{ animationDelay: "200ms" }}
          />
          <span
            className="mr-0.5 inline-block h-1 w-1 rounded bg-blue-500 [animation:typing-dot_1.5s_infinite_ease-in-out]"
            style={{ animationDelay: "300ms" }}
          />
          <span
            className="inline-block h-1 w-1 rounded bg-blue-500 [animation:typing-dot_1.5s_infinite_ease-in-out]"
            style={{ animationDelay: "400ms" }}
          />
        </div>
      </div>
    </div>
  </div>
);

const Chat = () => {
  const { characterId } = useParams();
  const {
    data: currentCharacter,
    isLoading,
    error,
  } = useCharacter(characterId || "");
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [newMessage, setNewMessage] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [turbo, setTurbo] = useLocalStorage(`turbo-${characterId}`, true);
  const [isRatingOpen, setIsRatingOpen] = useState(false);

  const { data: messages = [], isLoading: isHistoryLoading } = useChatHistory({
    characterId,
    userId: user?.id,
  });

  const messageScrollRef = useRef<HTMLDivElement>(null);
  const virtualizerRef = useRef<VirtualizerHandle>(null);

  const { data: readerStatus } = useQuery({
    queryKey: ["conversation-reader", user?.id, characterId],
    enabled: !!characterId && !!user?.id,
    queryFn: async () => {
      if (!characterId || !user?.id) return null;
      const { data, error } = await supabase
        .from("conversation_readers")
        .select("reader_status")
        .eq("character_id", characterId)
        .eq("profile_id", user.id)
        .single();
      if (error) throw error;
      return data?.reader_status ?? null;
    },
  });

  const firstRender = useIsFirstRender();

  useEffect(() => {
    if (virtualizerRef.current) {
      virtualizerRef.current.scrollToIndex(messages.length, {
        align: "end",
        smooth: !firstRender,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, readerStatus]);

  const mutation = useMutation({
    mutationFn: async (userMessage: string) => {
      if (!characterId || !user?.id) return;
      const { error } = await supabase.from("chat_history").insert([
        {
          character_id: characterId,
          profile_id: user.id,
          message: userMessage,
          sender: "user",
          turbo,
        },
      ]);
      if (error) throw error;
    },
    onSuccess: () => {
      if (user?.id) {
        queryClient.invalidateQueries({
          queryKey: ["chat-history", user.id, characterId],
        });
        queryClient.invalidateQueries({
          queryKey: ["conversations", user.id],
        });
      }
    },
    onSettled: () => {
      posthog.capture("message_sent", {
        character_id: characterId,
      });
    },
  });

  const handleSendMessage = () => {
    if (!newMessage.trim() || !currentCharacter) return;
    mutation.mutate(newMessage);
    setNewMessage("");
    inputRef.current?.focus();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const viewport = useVisualViewport();

  return (
    <div
      className="bg-linear-to-br relative flex h-full flex-col from-blue-50 via-purple-50 to-pink-50"
      style={{
        paddingTop: viewport?.offsetTop,
      }}
    >
      {/* Top Header */}
      <header
        className="shadow-lg/7 h-18 animate-slide-in-from-right absolute z-10 flex w-full items-center border-b border-white/20 bg-white/90 backdrop-blur-md"
        style={{
          top: viewport?.offsetTop ?? 0,
        }}
      >
        <div className="mx-auto flex w-full max-w-2xl flex-1 items-center space-x-3 px-4 py-3">
          <Button variant="ghost" asChild className="p-2">
            <Link to="/home">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          {isLoading || !currentCharacter ?
            <Skeleton className="h-9 w-9 animate-pulse rounded-full" />
          : <Link
              to={`/chat/${characterId}/details`}
              className="flex flex-1 items-center space-x-3 overflow-hidden rounded"
              tabIndex={0}
              aria-label={`Voir les détails de ${currentCharacter.name}`}
            >
              <CharacterAvatar
                name={currentCharacter.name}
                avatar_url={currentCharacter.avatar_url ?? undefined}
                className="h-9 w-9"
              />
              <div className="flex flex-1 flex-col justify-center overflow-hidden">
                <span className="font-bold leading-tight text-gray-900 hover:underline">
                  {currentCharacter.name}
                </span>
                <span className="block truncate text-sm leading-tight text-gray-600">
                  {currentCharacter.title}
                </span>
              </div>
            </Link>
          }
          {/* Turbo Mode 3-dot menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" aria-label="Chat options" className="p-2">
                <MoreVertical />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="flex w-full items-center justify-between space-x-2 px-2 py-1.5 text-sm text-gray-900">
                <span>Mode Turbo</span>
                <Switch
                  checked={turbo}
                  onCheckedChange={setTurbo}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <DropdownMenuItem
                onClick={() => setIsRatingOpen(true)}
                className="font-medium"
              >
                Donner mon avis
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Chat Area */}
      <div className="mx-auto flex min-h-0 w-full max-w-2xl flex-1 flex-col justify-end">
        {isHistoryLoading ?
          <div className="animate-fade-in flex flex-col items-center justify-center py-24 text-center text-gray-400">
            <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-indigo-600" />
          </div>
        : error || !currentCharacter || !user?.id || !characterId ?
          <div className="animate-fade-in flex flex-col items-center justify-center py-24 text-center text-gray-400">
            <p className="mb-4 text-red-600">
              Erreur lors du chargement des messages
            </p>
          </div>
        : messages.length === 0 ?
          <div className="animate-fade-in my-auto flex flex-col items-center justify-center px-4 text-center">
            <div className="mb-4 flex items-center gap-3 px-3 py-1">
              {/* <Send className="size-12 bg-sylvain-blue text-white p-3 leading-none shrink-0" /> */}
              <span className="font-medium">
                <mark className="bg-sylvain-blue box-decoration-clone p-0.5 px-1 leading-relaxed text-white">
                  Ici, c'est à vous d'envoyer le premier message !
                </mark>
              </span>
              {/* <Send className="h-8 w-8 bg-sylvain-blue text-white p-1" /> */}
            </div>
            {currentCharacter.first_message_helper_text && (
              <span className="text-foreground whitespace-pre-wrap text-center font-medium">
                {currentCharacter.first_message_helper_text}
              </span>
            )}
          </div>
        : <ScrollArea className="px-4 py-0" viewportRef={messageScrollRef}>
            <div
              style={{
                visibility: "hidden", // TODO replace with other optimization methods
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                minHeight: "100%",
              }}
            >
              <Virtualizer
                scrollRef={messageScrollRef}
                ref={virtualizerRef}
                overscan={10}
              >
                {messages.toReversed().map((message) => {
                  return (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      currentCharacterName={currentCharacter.name}
                      isLastMessage={message.id === messages[0].id}
                      userId={user?.id}
                      characterId={characterId}
                    />
                  );
                })}
                {readerStatus === "answering" && <AnsweringBubble />}
              </Virtualizer>
            </div>
          </ScrollArea>
        }
      </div>
      {/* Message Input */}
      <div className="bg-linear-to-tr pb-safe-or-4 shrink-0 from-blue-50 via-purple-50 to-pink-50 p-4 shadow-lg">
        <div className="mx-auto max-w-xl">
          <div className="flex items-end space-x-3">
            <ChatTextArea
              ref={inputRef}
              disabled={readerStatus === "answering"}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              rows={1}
              placeholder={
                currentCharacter ? `Message à ${currentCharacter.name}...` : ""
              }
            />
            <Button
              onClick={handleSendMessage}
              disabled={
                !newMessage.trim() ||
                mutation.isPending ||
                readerStatus === "answering"
              }
              aria-label="Envoyer"
              id="send-message-button"
              className={`bg-linear-to-r h-11 w-11 rounded-full from-indigo-500 to-purple-500 shadow-lg shadow-purple-300 hover:from-indigo-600 hover:to-purple-600`}
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
      <ChatRatingModal
        open={isRatingOpen}
        onOpenChange={setIsRatingOpen}
        characterId={characterId}
      />
    </div>
  );
};

export default Chat;
